import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Navigation from './components/Navigation';
import AuthWrapper from './components/AuthWrapper';
import ComplianceFooter from './components/ComplianceFooter';
import BetaSignupBanner from './components/BetaSignupBanner';
import { OrgSettingsProvider } from './contexts/OrgSettingsContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { UploadedSignalsProvider } from './contexts/UploadedSignalsContext';
import { deriveIsSuperAdmin } from './utils/rbac';
import { isCognitoConfigured } from './config/amplify';
import './App.css';

// 'landing' builds the marketing site (retainiq.cloud); anything else builds the
// authenticated app (app.retainiq.cloud). The two share no routes.
const IS_LANDING = process.env.REACT_APP_BUILD_TARGET === 'landing';

const LandingPage = React.lazy(() => import('./pages/LandingPage'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const KnowledgeGraph = React.lazy(() => import('./pages/KnowledgeGraph'));
const Interventions = React.lazy(() => import('./pages/Interventions'));
const Settings = React.lazy(() => import('./pages/Settings'));
const Pricing = React.lazy(() => import('./pages/Pricing'));
const SecurityPage = React.lazy(() => import('./pages/SecurityPage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));
const PrivacyPage = React.lazy(() => import('./pages/PrivacyPage'));
const TermsPage = React.lazy(() => import('./pages/TermsPage'));
const DemoPage = React.lazy(() => import('./pages/DemoPage'));
const ApplyPage = React.lazy(() => import('./pages/ApplyPage'));
const AdminConsole = React.lazy(() => import('./pages/AdminConsole'));

// Full-screen loader component
const FullScreenLoader: React.FC = () => {
  return (
    <div style={{
      minHeight: '100vh',
      background: '#0f172a',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <div style={{
        width: '40px',
        height: '40px',
        border: '4px solid #334155',
        borderTop: '4px solid #3b82f6',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
      }} />
    </div>
  );
};

// Fail-closed screen for an app build shipped without valid Cognito config.
// We deliberately render this instead of running the app with auth disabled.
const ConfigError: React.FC = () => (
  <div style={{
    minHeight: '100vh',
    background: '#0f172a',
    color: '#e2e8f0',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
    textAlign: 'center',
  }}>
    <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.75rem' }}>
      Configuration error
    </h1>
    <p style={{ color: '#94a3b8', maxWidth: '32rem', lineHeight: 1.6 }}>
      Authentication is not configured, so the application cannot start. Set
      {' '}<code>REACT_APP_COGNITO_USER_POOL_ID</code> and{' '}
      <code>REACT_APP_COGNITO_CLIENT_ID</code> to valid values and rebuild.
    </p>
  </div>
);

const PageWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="page-transition">{children}</div>
);

// Inner App component that uses auth context
const AppContent: React.FC = () => {
  const { isLoading, isAuthenticated, token } = useAuth();

  // Top-level loading guard - prevents route rendering until auth resolves
  if (isLoading) {
    return <FullScreenLoader />;
  }

  // App build with missing Cognito config: fail closed, never auth-disabled.
  if (!IS_LANDING && !isCognitoConfigured) {
    return <ConfigError />;
  }

  // App build, not signed in: render ONLY the sign-in page for every route —
  // no Dashboard, no marketing routes, no nav/footer chrome.
  if (!IS_LANDING && !isAuthenticated) {
    return (
      <Router>
        <Routes>
          <Route path="*" element={<AuthWrapper />} />
        </Routes>
      </Router>
    );
  }

  return (
    <Router>
      <div className="min-h-screen bg-slate-900 text-slate-100">
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:bg-indigo-600 focus:text-white focus:px-4 focus:py-2 focus:rounded"
        >
          Skip to main content
        </a>
        <Navigation />
        {IS_LANDING && <BetaSignupBanner isAuthenticated={isAuthenticated} />}
        <main id="main-content" className="max-w-[1920px] mx-auto px-6 lg:px-8 py-8">
          <Suspense fallback={<div className="flex items-center justify-center h-screen bg-slate-900"><div className="text-slate-400">Loading...</div></div>}>
            <Routes>
              {IS_LANDING ? (
                <>
                  {/* Marketing site — public, mounted only in the landing build */}
                  <Route path="/" element={<PageWrapper><LandingPage /></PageWrapper>} />
                  <Route path="/pricing" element={<PageWrapper><Pricing /></PageWrapper>} />
                  <Route path="/security" element={<PageWrapper><SecurityPage /></PageWrapper>} />
                  <Route path="/privacy" element={<PageWrapper><PrivacyPage /></PageWrapper>} />
                  <Route path="/terms" element={<PageWrapper><TermsPage /></PageWrapper>} />
                  <Route path="/demo" element={<PageWrapper><DemoPage /></PageWrapper>} />
                  <Route path="/apply" element={<PageWrapper><ApplyPage /></PageWrapper>} />
                  <Route path="/login" element={<AuthWrapper />} />
                  <Route path="*" element={<NotFoundPage />} />
                </>
              ) : (
                <>
                  {/* Authenticated app — only reachable when signed in */}
                  <Route path="/" element={<PageWrapper><Dashboard /></PageWrapper>} />
                  <Route path="/graph" element={<PageWrapper><KnowledgeGraph /></PageWrapper>} />
                  <Route path="/interventions" element={<PageWrapper><Interventions /></PageWrapper>} />
                  <Route path="/settings" element={<PageWrapper><Settings /></PageWrapper>} />
                  <Route
                    path="/admin"
                    element={
                      deriveIsSuperAdmin(token)
                        ? <PageWrapper><AdminConsole /></PageWrapper>
                        : <Navigate to="/" replace />
                    }
                  />
                  {/* An authenticated user who lands on /login (e.g. left over in
                      the URL from the unauthenticated branch during an in-flight
                      session restore) is sent to the dashboard, not 404'd. */}
                  <Route path="/login" element={<Navigate to="/" replace />} />
                  <Route path="*" element={<NotFoundPage />} />
                </>
              )}
            </Routes>
          </Suspense>
        </main>
        <ComplianceFooter />
      </div>
    </Router>
  );
};

function App() {
  return (
    <AuthProvider>
      <OrgSettingsProvider>
        <UploadedSignalsProvider>
          <AppContent />
        </UploadedSignalsProvider>
      </OrgSettingsProvider>
    </AuthProvider>
  );
}

export default App;
